import { useRef, useCallback } from 'react';
import { getAccessToken } from '../../apiHelpers';
import config from '../../config';
import useChatStore from '../../stores/useChatStore';

/**
 * Custom hook for consuming SSE streams from the Flerpie AI endpoint.
 *
 * Uses fetch() + ReadableStream instead of EventSource because:
 * - EventSource is GET-only, can't POST message body
 * - EventSource can't send Authorization headers (tokens are in memory, not cookies)
 *
 * Buffers stream chunks via requestAnimationFrame to prevent excessive
 * re-renders in React 17 (no automatic batching).
 */
const useSSEStream = () => {
  const bufferRef = useRef('');
  const rafRef = useRef(null);

  const flushBuffer = useCallback(() => {
    if (bufferRef.current) {
      const chunk = bufferRef.current;
      bufferRef.current = '';
      useChatStore.getState().appendStreamChunk(chunk);
    }
    rafRef.current = null;
  }, []);

  const scheduleFlush = useCallback(() => {
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(flushBuffer);
    }
  }, [flushBuffer]);

  const sendMessage = useCallback(async (conversationId, content) => {
    const store = useChatStore.getState();

    // Cancel any existing stream before starting a new one
    if (store.isStreaming) {
      store.cancelStream();
      // Also clear local RAF state from previous stream
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      bufferRef.current = '';
    }

    // Add user message optimistically
    store.addUserMessage(content);

    // Create conversation if none exists
    let convId = conversationId;
    if (!convId) {
      await store.createConversation();
      convId = useChatStore.getState().activeConversationId;
      if (!convId) return; // Failed to create
    }

    const abortController = new AbortController();
    store.startStreaming(abortController);

    try {
      const token = getAccessToken();
      const response = await fetch(
        `${config.API_ENDPOINT}/ai/conversations/${convId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          credentials: 'include',
          body: JSON.stringify({ content, page: window.location.pathname }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        let friendlyMsg;
        try {
          const parsed = JSON.parse(errText);
          friendlyMsg = parsed?.error?.message;
        } catch { /* not JSON */ }
        throw new Error(friendlyMsg || `Something went wrong (${response.status}). Try again?`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let partial = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        partial += decoder.decode(value, { stream: true });

        // SSE protocol: events separated by double newlines
        const events = partial.split('\n\n');
        // Last element may be incomplete — keep it for next iteration
        partial = events.pop() || '';

        for (const event of events) {
          if (!event.trim()) continue;

          // Parse event type and data
          let eventType = null;
          let eventData = '';

          for (const line of event.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData += line.slice(6);
            }
          }

          if (!eventData) continue;

          try {
            const data = JSON.parse(eventData);

            switch (eventType || data.type) {
              case 'thinking':
                useChatStore.getState().setEmotion(data.emotion || 'thinking');
                break;

              case 'token':
                bufferRef.current += data.text;
                scheduleFlush();
                break;

              case 'emotion':
                useChatStore.getState().setEmotion(data.value || data.emotion);
                break;

              case 'retrying':
                useChatStore.getState().setEmotion('confused');
                break;

              case 'progress':
                // A lookup is running; its label shows in the empty bubble.
                useChatStore.getState().setProgressLabel(data.label || null);
                break;

              case 'done': {
                // Flush any remaining buffer
                if (bufferRef.current) {
                  cancelAnimationFrame(rafRef.current);
                  flushBuffer();
                }
                // Finalize with the complete message from server
                const message = data.message || data;
                useChatStore.getState().finalizeStream({
                  id: message.id,
                  role: 'assistant',
                  content: message.content,
                  emotion: message.emotion || 'neutral',
                  sql_generated: message.sql_generated,
                  visualization: message.visualization,
                  navigate: message.navigate || null,
                  note: message.note || null,
                  suggestion: message.suggestion || null,
                  sources: message.sources || null,
                  remember: message.remember || null,
                  created_at: message.created_at || new Date().toISOString(),
                });
                // Update conversation title if provided
                if (message.conversation_title) {
                  useChatStore.getState().updateConversationTitle(convId, message.conversation_title);
                }
                break;
              }

              case 'error':
                if (data.isRateLimit) useChatStore.getState().startCooldown(60);
                throw new Error(data.message || 'Flerpie encountered an error');

              default:
                break;
            }
          } catch (parseErr) {
            // If it's our thrown error, re-throw
            if (!(parseErr instanceof SyntaxError)) {
              throw parseErr;
            }
            console.warn('Failed to parse SSE event:', eventData);
          }
        }
      }
    } catch (err) {
      // Clean up any pending RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      bufferRef.current = '';

      if (err.name === 'AbortError') return; // User cancelled

      console.error('Flerpie stream error:', err);

      // Show error as a message
      useChatStore.getState().finalizeStream({
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: err.message,
        emotion: 'concerned',
        created_at: new Date().toISOString(),
      });
    }
  }, [scheduleFlush, flushBuffer]);

  return { sendMessage };
};

export default useSSEStream;
